import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trash2, ExternalLink, Check, X, Archive as ArchiveIcon, Sparkles, RefreshCw } from "lucide-react";

const STATUSES = ["all", "pending_review", "published", "rejected", "archived", "draft"];

export default function AdminRecipes() {
  const [status, setStatus] = useState("pending_review");
  const [data, setData] = useState({ counts: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [aiStatus, setAiStatus] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const loadAiStatus = async () => {
    try {
      const { data } = await http.get("/admin/recipes/regenerate-summaries/status");
      setAiStatus(data);
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/recipes", { params: { status, limit: 200 } });
      setData(data);
    } catch { toast.error("Failed to load recipes"); }
    finally { setLoading(false); }
    loadAiStatus();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Poll AI status while a bulk regen is likely running
  useEffect(() => {
    if (!regenerating) return;
    const iv = setInterval(loadAiStatus, 4000);
    return () => clearInterval(iv);
  }, [regenerating]);

  const regenerate = async (mode) => {
    const label = mode === "all" ? "ALL published recipes" : "recipes with no AI summary";
    if (!window.confirm(`Regenerate AI summaries for ${label}? This runs in the background using Claude Sonnet 4.5.`)) return;
    setRegenerating(true);
    try {
      const { data } = await http.post(`/admin/recipes/regenerate-summaries?mode=${mode}`);
      toast.success(`Queued for ${data.target} recipes — poll below for progress`);
      // stop polling after ~4 min (worst case for 21 recipes at ~5s each)
      setTimeout(() => setRegenerating(false), 240000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to queue");
      setRegenerating(false);
    }
  };

  const moderate = async (r, next) => {
    let comment = null;
    if (next === "rejected") {
      comment = prompt("Reason for rejection (shown to the author):");
      if (comment === null) return;
    }
    try {
      await http.patch(`/admin/recipes/${r.id}/moderate`, { status: next, comment });
      toast.success(`Moved to ${next}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const feature = async (r) => {
    try {
      await http.patch(`/admin/recipes/${r.id}/feature`, { featured: !r.featured });
      toast.success(r.featured ? "Unfeatured" : "Featured");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}" permanently?`)) return;
    try { await http.delete(`/admin/recipes/${r.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div data-testid="admin-recipes-loading" className="py-12" style={{ color: "var(--wofy-text-soft)" }}>Loading recipes…</div>;

  return (
    <div data-testid="admin-recipes">
      {/* AI summary toolbar */}
      <div className="mb-5 p-4 border rounded-lg flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--wofy-line)", background: "rgba(196,127,58,0.08)" }}>
        <div className="text-sm">
          <div className="font-semibold flex items-center gap-2">
            <Sparkles size={14} style={{ color: "#c47f3a" }} /> AI recipe summaries (Claude Sonnet 4.5)
          </div>
          {aiStatus ? (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              {aiStatus.total_published} published · {aiStatus.missing} still missing a summary
              {aiStatus.last_run && ` · last run: ${new Date(aiStatus.last_run.ran_at).toLocaleString()} (${aiStatus.last_run.processed}/${aiStatus.last_run.target} processed, mode: ${aiStatus.last_run.mode})`}
            </div>
          ) : (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>Loading status…</div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-recipes-regen-missing"
            onClick={() => regenerate("missing")}
            disabled={regenerating || (aiStatus && aiStatus.missing === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#eee" : "#c47f3a", color: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#888" : "white" }}
          >
            <RefreshCw size={13} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Running…" : `Generate for missing${aiStatus ? ` (${aiStatus.missing})` : ""}`}
          </button>
          <button
            data-testid="admin-recipes-regen-all"
            onClick={() => regenerate("all")}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <Sparkles size={13} /> Regenerate ALL
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`admin-recipes-status-${s}`}
            onClick={() => setStatus(s)}
            className="px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wide"
            style={{
              borderColor: "var(--wofy-line)",
              background: status === s ? "var(--wofy-primary)" : "transparent",
              color: status === s ? "white" : "var(--wofy-text)",
            }}
          >
            {s.replace("_", " ")} ({data.counts[s] || 0})
          </button>
        ))}
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left" style={{ borderBottom: "1px solid var(--wofy-line)" }}>
            <th className="py-2 pr-2">Recipe</th>
            <th className="py-2 pr-2">Author</th>
            <th className="py-2 pr-2">Cuisine · Category</th>
            <th className="py-2 pr-2 text-center">Rating</th>
            <th className="py-2 pr-2 text-center">Featured</th>
            <th className="py-2 pr-2 text-center">Status</th>
            <th className="py-2 pr-2"></th>
          </tr>
        </thead>
        <tbody>
          {data.items.length === 0 && (
            <tr><td colSpan="7" className="py-6 text-center" style={{ color: "var(--wofy-text-soft)" }}>No recipes in this bucket.</td></tr>
          )}
          {data.items.map((r) => (
            <tr key={r.id} data-testid={`admin-recipe-row-${r.id}`} className="border-b" style={{ borderColor: "var(--wofy-line)" }}>
              <td className="py-2 pr-2">
                <div className="flex items-center gap-2">
                  {r.cover_photo && <img src={r.cover_photo} alt="" className="w-12 h-12 rounded object-cover" />}
                  <div>
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{r.short_description?.slice(0, 80)}</div>
                  </div>
                </div>
              </td>
              <td className="py-2 pr-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{r.author_name}</td>
              <td className="py-2 pr-2 text-xs">{r.cuisine} · {r.category}</td>
              <td className="py-2 pr-2 text-center text-xs">{(r.rating_avg || 0).toFixed(1)} ({r.rating_count || 0})</td>
              <td className="py-2 pr-2 text-center">
                <button data-testid={`admin-recipe-feature-${r.id}`} onClick={() => feature(r)} title={r.featured ? "Unfeature" : "Feature"}>
                  <Star size={16} style={{ color: r.featured ? "#c47f3a" : "#c8c1af", fill: r.featured ? "#c47f3a" : "transparent" }} />
                </button>
              </td>
              <td className="py-2 pr-2 text-center text-xs uppercase tracking-wider">{r.status?.replace("_", " ")}</td>
              <td className="py-2 pr-2">
                <div className="flex gap-1 justify-end flex-wrap">
                  {r.slug && r.status === "published" && (
                    <Link to={`/recipes/${r.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 border rounded" style={{ borderColor: "var(--wofy-line)" }}>
                      <ExternalLink size={13} />
                    </Link>
                  )}
                  {r.status !== "published" && (
                    <button data-testid={`admin-recipe-approve-${r.id}`} onClick={() => moderate(r, "published")} className="p-1.5 border rounded" style={{ borderColor: "var(--wofy-line)", color: "#5C6B52" }} title="Publish"><Check size={13} /></button>
                  )}
                  {r.status !== "rejected" && (
                    <button data-testid={`admin-recipe-reject-${r.id}`} onClick={() => moderate(r, "rejected")} className="p-1.5 border rounded" style={{ borderColor: "var(--wofy-line)", color: "#b23a3a" }} title="Reject"><X size={13} /></button>
                  )}
                  {r.status !== "archived" && (
                    <button onClick={() => moderate(r, "archived")} className="p-1.5 border rounded" style={{ borderColor: "var(--wofy-line)" }} title="Archive"><ArchiveIcon size={13} /></button>
                  )}
                  <button data-testid={`admin-recipe-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 border rounded" style={{ borderColor: "var(--wofy-line)", color: "#b23a3a" }} title="Delete"><Trash2 size={13} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
